import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { RansomNoteConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { tx, txr } from "../lib/tx";

function fmtCountdown(total: number) {
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((n) => n.toString().padStart(2, "0")).join(":");
}

/**
 * The ransomware takeover: the monitor floods red, a padlock + a live ticking
 * countdown + the payment demand + a scroll of "encrypted" filenames. There is
 * no choice left — the damage is done — so this is a cinematic beat that ends on
 * "What happens next?". A short, sharp glitch sting plays on arrival.
 */
export function RansomNote({
  config,
  onContinue,
}: {
  config: RansomNoteConfig;
  onContinue: () => void;
}) {
  const [left, setLeft] = useState(config.deadlineSeconds ?? 48 * 3600);
  const [reveal, setReveal] = useState(false);
  const mounted = useRef(true);
  const screenRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.6);

  // onScreen: the note is laid out at a fixed 1040x640 "desktop" and scaled
  // to the monitor box, so it reads as the PC's own screen at any window size.
  useLayoutEffect(() => {
    if (!config.onScreen) return;
    const el = screenRef.current;
    if (!el) return;
    const fit = () => {
      if (el.clientWidth && el.clientHeight) setScale(Math.min(el.clientWidth / 1040, el.clientHeight / 640));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [config.onScreen]);

  useEffect(() => {
    mounted.current = true;
    // a brief tense sting (reuse the buzzer rasp as an alarm).
    audio.buzz();
    const t = window.setTimeout(() => mounted.current && setReveal(true), 900);
    const tick = window.setInterval(
      () => mounted.current && setLeft((v) => (v > 0 ? v - 1 : 0)),
      1000
    );
    return () => {
      mounted.current = false;
      window.clearTimeout(t);
      window.clearInterval(tick);
    };
  }, []);

  const files = config.fileList ?? [
    "Q3_financials.xlsx", "client_database.bak", "payroll_2026.xlsx",
    "board_minutes.docx", "loan_applications.pdf", "kyc_scans.zip",
  ];

  const content = (
    <>
      <div className="ransom-glitch" />
      <div className="ransom-inner">
        <div className="ransom-lock" aria-hidden>{"\u{1F512}"}</div>
        <h1 className="ransom-title">{config.title ?? tx("YOUR FILES HAVE BEEN ENCRYPTED")}</h1>
        {config.org && <div className="ransom-org">{config.org}</div>}
        <p className="ransom-body">{config.body}</p>

        <div className="ransom-grid">
          <div className="ransom-demand">
            <div className="ransom-demand-h">{tx("Decryption fee")}</div>
            <div className="ransom-amount">{config.amount}</div>
            {config.wallet && (
              <>
                <div className="ransom-wallet-l">{config.walletLabel ?? tx("Send payment to:")}</div>
                <div className="ransom-wallet">{config.wallet}</div>
              </>
            )}
          </div>
          <div className="ransom-timer">
            <div className="ransom-demand-h">{tx("Time remaining")}</div>
            <div className="ransom-clock">{fmtCountdown(left)}</div>
            <div className="ransom-warn">{tx("Files deleted when the timer hits zero.")}</div>
          </div>
        </div>

        <div className="ransom-files">
          <div className="ransom-files-h">{tx("Encrypting")}</div>
          <div className="ransom-files-scroll">
            {files.concat(files).map((f, i) => (
              <span key={i} className="ransom-file">
                <span className="ransom-x">{"\u2717"}</span> {txr("{f}.locked", { f })}
              </span>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  const continueBtn = reveal && (
    <button className={`btn ransom-continue${config.onScreen ? " ransom-pc-continue" : ""}`} onClick={onContinue}>
      {config.continueLabel ?? tx("What happens next?")}
    </button>
  );

  if (config.onScreen) {
    return (
      <div className="ransom-pc">
        <div className="ransom-pc-screen" ref={screenRef}>
          <div className="ransom ransom--virtual" style={{ transform: `translate(-50%, -50%) scale(${scale})` }}>
            {content}
            <div className="ransom-taskbar" aria-hidden>
              <span className="ransom-tb-start" />
              {config.processName && <span className="ransom-tb-app">{config.processName}</span>}
              <span className="ransom-tb-clock">{config.clock ?? ""}</span>
            </div>
          </div>
        </div>
        {continueBtn}
      </div>
    );
  }

  return (
    <div className="ransom">
      {content}
      {continueBtn}
    </div>
  );
}
